import type { GameData, Participant, ParticipantId } from "./model.ts";

/**
 * The hunted.
 *
 * A match with no clock has no shape: nothing builds, nothing resolves, and the
 * player in front is as safe as the player who just arrived. This is the smallest
 * mechanic that gives an endless arena an arc — whoever is winning is marked, is
 * worth double to everyone else, and is what the bots come for.
 *
 * The consequence worth stating plainly: leading is no longer a place to rest. It
 * is the most dangerous position in the room, and it is the one everybody can see.
 *
 * Derived from scores each tick rather than stored as a claim, so nothing can leave
 * a stale crown on a core that has since died or been overtaken.
 */

/**
 * How much score earns the mark.
 *
 * Measured over five minutes in a twenty-bot room: the leader sits around 2600 and
 * the runner-up around 1900. At 1800 the mark is on somebody almost all the time
 * without landing on whoever happens to be ahead ten seconds into a match.
 */
export const BOUNTY_MIN_SCORE = 1800;

/**
 * How far ahead a challenger must be to take the mark, and how long it stays put
 * once it moves.
 *
 * The gap between first and second is often only 1.1x, so without both of these the
 * crown changes hands several times a second in a close race and reads as a
 * flickering bug rather than a contest.
 */
export const BOUNTY_LEAD_MARGIN = 1.08;
export const BOUNTY_HOLD_SECONDS = 4;

/** What a hit on the hunted is worth, against the same hit on anybody else. */
export const BOUNTY_MULTIPLIER = 2;

/**
 * How much closer the marked core looks to a bot than it is, in pixels.
 *
 * A discount rather than an override: a bot with somebody in its face does not turn
 * its back and set off across the arena, but between two comparable targets it goes
 * for the one worth double. Which is what a person would do.
 */
export const BOUNTY_LURE = 320;

export function isBounty(game: GameData, participant: Participant | ParticipantId) {
  const id = typeof participant === "string" ? participant : participant.id;
  return game.bounty !== null && game.bounty === id;
}

/** Score for landing a hit, doubled when the victim is the one everyone is hunting. */
export function bountyReward(base: number, hunted: boolean) {
  return hunted ? base * BOUNTY_MULTIPLIER : base;
}

function eligible(participant: Participant) {
  return participant.alive && participant.score >= BOUNTY_MIN_SCORE;
}

/**
 * Recomputes who is marked. Returns the new holder when it changed, so the caller
 * can announce it; returns undefined when nothing moved.
 *
 * The holder is dropped the instant they die or fall below the threshold — a mark
 * held open for a few seconds "for stability" would point at a corpse.
 */
export function updateBounty(game: GameData, dt: number): ParticipantId | null | undefined {
  game.bountyHold = Math.max(0, (game.bountyHold ?? 0) - Math.max(0, dt));

  const holder = game.bounty === null
    ? undefined
    : game.participants.find((participant) => participant.id === game.bounty);

  if (holder && !eligible(holder)) {
    game.bounty = null;
    game.bountyHold = 0;
    return null;
  }

  let leader: Participant | undefined;
  for (const participant of game.participants) {
    if (!eligible(participant)) continue;
    if (!leader || participant.score > leader.score) leader = participant;
  }

  if (!leader) {
    if (game.bounty === null) return undefined;
    game.bounty = null;
    game.bountyHold = 0;
    return null;
  }

  if (!holder) {
    game.bounty = leader.id;
    game.bountyHold = BOUNTY_HOLD_SECONDS;
    return leader.id;
  }

  if (leader.id === holder.id) return undefined;
  if (game.bountyHold > 0) return undefined;
  if (leader.score < holder.score * BOUNTY_LEAD_MARGIN) return undefined;

  game.bounty = leader.id;
  game.bountyHold = BOUNTY_HOLD_SECONDS;
  return leader.id;
}
